import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { entities, getEntity, type EntityKey } from './schema';
import { exportCSV, exportExcel, exportTemplateCSV, buildFileName } from './utils';
import ImportPanel from './components/ImportPanel';
import { useOffice } from '@/hooks/useOffice';

export default function ImportExportPage() {
  const { currentOfficeId } = useOffice();
  const [active, setActive] = useState<EntityKey>('tapes');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const entity = getEntity(active);
  const headers = entity.columns.map((c) => c.label);

  const load = async (key: EntityKey) => {
    if (!currentOfficeId) return;
    const cfg = getEntity(key);
    setLoading(true);
    setErr('');
    try {
      const { data, error } = await supabase.from(cfg.table).select('*').eq('office_id', currentOfficeId).order('id', { ascending: true });
      if (error) throw error;
      setRows((data || []) as Record<string, unknown>[]);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(active); }, [active, currentOfficeId]);

  const exportRows = rows.map((r) => {
    const o: Record<string, unknown> = {};
    entity.columns.forEach((c) => { o[c.label] = r[c.key] ?? ''; });
    return o;
  });

  const prefix = entity.singular.toUpperCase();

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Import / Export</h1>
        <p className="text-sm text-slate-500 mt-1">
          Bulk import and export all system data as CSV or Excel files.
        </p>
      </div>

      <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1 mb-6">
        {entities.map((e) => (
          <button
            key={e.key}
            onClick={() => setActive(e.key)}
            className={`px-4 py-1.5 rounded-full text-sm cursor-pointer whitespace-nowrap transition ${
              active === e.key ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            {e.label}
          </button>
        ))}
      </div>

      {err && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
          <span>{err}</span>
          <button onClick={() => load(active)} className="text-xs underline cursor-pointer">Retry</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-1">
            <i className={`${entity.icon} w-5 h-5 flex items-center justify-center text-slate-500`} />
            <h2 className="font-semibold text-sm">Export {entity.label}</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Download the current {entity.label.toLowerCase()} as a spreadsheet.
          </p>

          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-md mb-4">
            <span className="text-xs text-slate-500">Total records</span>
            <span className="text-sm font-semibold">
              {loading ? '...' : rows.length}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => exportCSV(buildFileName(prefix, 'csv'), headers, exportRows)}
              disabled={loading}
              className="flex-1 min-w-[120px] px-4 py-2 text-sm rounded-md bg-slate-900 text-white cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <i className="ri-download-line w-4 h-4 flex items-center justify-center" /> Export CSV
            </button>
            <button
              onClick={() => exportExcel(buildFileName(prefix, 'xlsx'), headers, exportRows)}
              disabled={loading}
              className="flex-1 min-w-[120px] px-4 py-2 text-sm rounded-md border border-slate-200 cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <i className="ri-file-excel-2-line w-4 h-4 flex items-center justify-center" /> Export Excel
            </button>
          </div>

          <button
            onClick={() => exportTemplateCSV(buildFileName(`${prefix}_TEMPLATE`, 'csv'), entity.columns, entity.example)}
            className="mt-3 text-xs text-slate-500 hover:text-slate-900 cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-file-list-3-line w-4 h-4 flex items-center justify-center" />
            Download empty template (CSV)
          </button>
        </div>

        {/* Import */}
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-upload-cloud-2-line w-5 h-5 flex items-center justify-center text-slate-500" />
            <h2 className="font-semibold text-sm">Import {entity.label}</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Upload a CSV or Excel file. Columns are matched automatically by header name.
          </p>
          <ImportPanel key={entity.key} entity={entity} onImported={() => load(active)} />
        </div>
      </div>
    </div>
  );
}